import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { QuantityAndUnitInput } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { PriceAndTaxClassInput } from '@/shared/inputs/PriceAndTaxClassInput.tsx';
import { NegativeSalesPriceHint, ZeroSalesPriceHint } from '@/shared/components/PriceInputHints.tsx';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { usePositionFormQuantities } from '@/shared/forms/AutomaticProductsForm/hooks/usePositionFormQuantities.ts';

export function PositionSalesMaterialFieldset() {
    const { t } = useTranslation();
    const { taxClassOptions, customerOrganization, formRules } = useAutomaticFormContext();
    const methods = useFormContext<AutomaticProductsFormValues>();
    const { register, watch, setValue } = methods;
    const { onSalesMaterialQuantityChange, onSalesMaterialUnitChange } = usePositionFormQuantities(methods);

    const [salesMaterialPrice, salesMaterialUnit, currencyCode, showZeroSalesMaterialPrice] = watch([
        'salesMaterialPrice',
        'salesMaterialUnit',
        'currencyCode',
        'showZeroSalesMaterialPrice',
    ]);

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.material')}
            dataTest="position-sales-material-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-2 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn className="@2xl:col-span-5">
                    <div className="grid grid-cols-2 gap-1">
                        <FieldsetLabel>{t('forms.automaticProductsForm.labels.quantity')}</FieldsetLabel>
                        <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    </div>
                    <QuantityAndUnitInput
                        quantityProps={{
                            ...register('salesMaterialQuantity'),
                            onQuantityChange: qty => onSalesMaterialQuantityChange(qty),
                        }}
                        unitProps={{
                            ...register('salesMaterialUnit'),
                            supportedUnitNames: [...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options],
                            variant: 'lg',
                            disabled: formRules?.salesMaterialUnit?.disabled,
                            onUnitChange: unit => onSalesMaterialUnitChange(toAutomaticUnit(unit)),
                        }}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="@2xl:col-span-3">
                    <div className="grid grid-cols-3 gap-1">
                        <FieldsetLabel className="col-span-2">
                            {t('forms.automaticProductsForm.labels.unitPrice')}
                        </FieldsetLabel>
                        <FieldsetLabel className="col-span-1">
                            {t('forms.automaticProductsForm.labels.taxClass')}
                        </FieldsetLabel>
                    </div>
                    <PriceAndTaxClassInput
                        priceProps={{
                            ...register('salesMaterialPrice'),
                            unit: salesMaterialUnit,
                            currencyCode,
                            testId: 'sales-material-price-input',
                        }}
                        taxClassProps={{
                            ...register('salesMaterialTaxClassId'),
                            options: taxClassOptions,
                            testId: 'sales-material-tax-class-input',
                            disabled: formRules?.salesMaterialTaxClassId?.disabled,
                        }}
                    />
                </FieldsetColumn>
                {Number(salesMaterialPrice) === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroSalesPriceHint
                            orderingOrganizationName={customerOrganization.name}
                            isBillingRelevant={showZeroSalesMaterialPrice ?? false}
                            onClick={() =>
                                setValue('showZeroSalesMaterialPrice', !(showZeroSalesMaterialPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(salesMaterialPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativeSalesPriceHint orderingOrganizationName={customerOrganization.name} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
